import React, { useState, useEffect } from 'react';
import { Card, Statistic, Row, Col, Table, Button, message, Spin, Modal, Tag, Typography, Badge, List } from 'antd';
import { PlusOutlined, PaperClipOutlined, ExclamationCircleOutlined, DownloadOutlined, EyeOutlined } from '@ant-design/icons';
import FilePreview from '../shared/FilePreview';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import {
  getFinanceSummary,
  getFinanceRecords,
  deleteFinanceRecord,
  updateContractAmount,
  EntityType,
  type FinanceSummary,
  type FinanceRecord,
} from '../../../../services/finance';
import AddCaseFinanceRecordModal from './AddCaseFinanceRecordModal';

const { Text } = Typography;

const { confirm } = Modal;

interface CaseFinanceTabProps {
  caseId: number;
  caseData: any;
  canEdit: boolean;
}

/**
 * 案件财务记录Tab组件
 * 显示财务汇总和财务记录列表
 * 参考LeadDrawer的FinanceTab实现
 */
const CaseFinanceTab: React.FC<CaseFinanceTabProps> = ({ caseId, canEdit }) => {
  const [summary, setSummary] = useState<FinanceSummary | null>(null);
  const [records, setRecords] = useState<FinanceRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [attachmentModalVisible, setAttachmentModalVisible] = useState(false);
  const [selectedAttachments, setSelectedAttachments] = useState<any[]>([]);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewFile, setPreviewFile] = useState<any>(null);

  /**
   * 加载财务汇总数据
   */
  const loadFinanceSummary = async () => {
    try {
      const data = await getFinanceSummary(EntityType.CASE, caseId);
      // Convert string amounts to numbers if needed
      const normalizedSummary: FinanceSummary = {
        contract_amount: typeof data.contract_amount === 'string' 
          ? parseFloat(data.contract_amount) 
          : data.contract_amount,
        total_received: typeof data.total_received === 'string' 
          ? parseFloat(data.total_received) 
          : data.total_received,
        total_refund: typeof data.total_refund === 'string' 
          ? parseFloat(data.total_refund) 
          : data.total_refund,
        total_receivable: typeof data.total_receivable === 'string' 
          ? parseFloat(data.total_receivable) 
          : data.total_receivable,
        net_received: typeof data.net_received === 'string' 
          ? parseFloat(data.net_received) 
          : data.net_received,
      };
      setSummary(normalizedSummary);
    } catch (error: any) {
      console.error('Failed to load finance summary:', error);
      // 不显示错误消息，因为可能还没有财务数据
    }
  };

  /**
   * 加载财务记录列表
   */
  const loadFinanceRecords = async () => {
    try {
      const data = await getFinanceRecords(EntityType.CASE, caseId);
      // Convert string amounts to numbers if needed and ensure required fields
      const normalizedRecords: FinanceRecord[] = (data.items || []).map(record => ({
        ...record,
        amount: typeof record.amount === 'string' 
          ? parseFloat(record.amount) 
          : record.amount,
        updated_at: record.updated_at || record.created_at,
      }));
      setRecords(normalizedRecords);
    } catch (error: any) {
      console.error('Failed to load finance records:', error);
      message.error('加载财务记录失败');
    }
  };

  /**
   * 加载所有财务数据
   */
  const loadFinanceData = async () => {
    setLoading(true);
    try {
      await Promise.all([loadFinanceSummary(), loadFinanceRecords()]);
    } finally {
      setLoading(false);
    }
  };

  /**
   * 刷新财务数据
   */
  const refreshFinanceData = () => {
    loadFinanceData();
  };

  /**
   * 处理删除财务记录
   */
  const handleDeleteRecord = (record: FinanceRecord) => {
    confirm({
      title: '确认删除',
      icon: <ExclamationCircleOutlined />,
      content: `确定要删除这条财务记录吗？`,
      okText: '确认',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          await deleteFinanceRecord(EntityType.CASE, caseId, record.id);
          message.success('财务记录已删除');
          refreshFinanceData();
        } catch (error: any) {
          console.error('Failed to delete finance record:', error);
          message.error('删除失败，请重试');
        }
      },
    });
  };

  /**
   * 处理打开添加财务记录弹窗
   */
  const handleOpenAddModal = () => {
    setModalVisible(true);
  };

  /**
   * 处理关闭添加财务记录弹窗
   */
  const handleCloseAddModal = () => {
    setModalVisible(false);
  };

  /**
   * 处理添加成功
   */
  const handleAddSuccess = () => {
    refreshFinanceData();
    handleCloseAddModal();
  };

  /**
   * 格式化金额显示
   */
  const formatAmount = (amount: number | string, isRefund: boolean = false): React.ReactNode => {
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    const formattedAmount = numAmount.toLocaleString('zh-CN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    return (
      <Text style={{ color: isRefund ? '#cf1322' : '#000' }}>
        {isRefund ? '-' : ''}¥{formattedAmount}
      </Text>
    );
  };

  /**
   * 处理查看附件
   */
  const handleViewAttachments = (attachments: any[]) => {
    setSelectedAttachments(attachments);
    setAttachmentModalVisible(true);
  };

  /**
   * 处理预览附件
   */
  const handlePreviewAttachment = async (attachment: any) => {
    try {
      // 动态导入getAttachmentDownloadUrl
      const { getAttachmentDownloadUrl } = await import('../../../../services/finance');
      
      // 获取临时下载URL
      const response = await getAttachmentDownloadUrl(
        EntityType.CASE,
        caseId,
        attachment.id
      );
      
      // 使用 FilePreview 组件预览
      setPreviewFile({
        file_url: response.download_url,
        original_filename: attachment.file_name,
        content_type: attachment.file_type,
      });
      setPreviewVisible(true);
      // 关闭附件列表弹窗
      setAttachmentModalVisible(false);
    } catch (error) {
      console.error('Failed to get attachment URL:', error);
      message.error('获取附件链接失败');
    }
  };

  /**
   * 处理下载附件
   */
  const handleDownloadAttachment = async (attachment: any) => {
    try {
      // 动态导入getAttachmentDownloadUrl
      const { getAttachmentDownloadUrl } = await import('../../../../services/finance');
      
      // 获取临时下载URL
      const response = await getAttachmentDownloadUrl(
        EntityType.CASE,
        caseId,
        attachment.id
      );
      
      // 在新窗口打开下载链接
      window.open(response.download_url, '_blank');
      message.success('开始下载');
    } catch (error) {
      console.error('Failed to download attachment:', error);
      message.error('下载失败');
    }
  };

  // 组件加载时获取财务数据
  useEffect(() => {
    if (caseId) {
      loadFinanceData();
    }
  }, [caseId]);

  // 表格列定义
  const columns: ColumnsType<FinanceRecord> = [
    {
      title: '费用类型',
      dataIndex: 'expense_type',
      key: 'expense_type',
      width: 100,
      render: (type: string) => {
        const typeMap: Record<string, string> = {
          CONSULTATION_FEE: '咨询费',
          LAWYER_FEE: '律师费',
          CONSULTANT_FEE: '顾问费',
          SERVICE_FEE: '服务费',
          TRAVEL_FEE: '差旅费',
          OTHER: '其他',
        };
        return typeMap[type] || type;
      },
    },
    {
      title: '款项性质',
      dataIndex: 'payment_nature',
      key: 'payment_nature',
      width: 100,
      render: (nature: string) => {
        const natureMap: Record<string, { text: string; color: string }> = {
          RECEIVED: { text: '已收款', color: 'success' },
          RECEIVABLE: { text: '应收款', color: 'warning' },
          REFUND: { text: '退款', color: 'error' },
        };
        const config = natureMap[nature] || { text: nature, color: 'default' };
        return <Tag color={config.color}>{config.text}</Tag>;
      },
    },
    {
      title: '动款金额',
      dataIndex: 'amount',
      key: 'amount',
      width: 120,
      align: 'right',
      render: (amount: number, record: FinanceRecord) => {
        const isRefund = record.payment_nature === 'REFUND';
        return formatAmount(amount, isRefund);
      },
    },
    {
      title: '动款时间',
      dataIndex: 'payment_time',
      key: 'payment_time',
      width: 160,
      render: (time: string) => 
        time ? dayjs(time).format('YYYY-MM-DD HH:mm') : <Text type="secondary">-</Text>,
    },
    {
      title: '备注',
      dataIndex: 'remark',
      key: 'remark',
      ellipsis: true,
      render: (remark: string) => remark || <Text type="secondary">-</Text>,
    },
    {
      title: '附件',
      dataIndex: 'attachments',
      key: 'attachments',
      width: 80,
      align: 'center',
      render: (attachments: any[]) => {
        if (!attachments || attachments.length === 0) {
          return <Text type="secondary">-</Text>;
        }

        return (
          <div 
            style={{ cursor: 'pointer', display: 'inline-block' }}
            onClick={(e) => {
              e.stopPropagation();
              handleViewAttachments(attachments);
            }}
          >
            <Badge count={attachments.length} showZero={false}>
              <PaperClipOutlined style={{ fontSize: 16 }} />
            </Badge>
          </div>
        );
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 80,
      fixed: 'right',
      render: (_, record) => (
        canEdit && (
          <Button
            type="link"
            size="small"
            danger
            onClick={() => handleDeleteRecord(record)}
          >
            删除
          </Button>
        )
      ),
    },
  ];

  // 显示加载状态
  if (loading && records.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 0' }}>
        <Spin size="large" tip="加载财务数据中..." />
      </div>
    );
  }

  return (
    <div className="case-finance-tab">
      {/* 财务汇总卡片 */}
      <Card
        title="财务汇总"
        style={{ marginBottom: 24 }}
        bordered={false}
        extra={
          canEdit && (
            <Button
              type="link"
              onClick={async () => {
                const amount = prompt('请输入合同金额：', String(summary?.contract_amount || 0));
                if (amount !== null && !isNaN(parseFloat(amount))) {
                  try {
                    await updateContractAmount(EntityType.CASE, caseId, parseFloat(amount));
                    message.success('合同金额更新成功');
                    refreshFinanceData();
                  } catch (error) {
                    message.error('更新失败，请重试');
                  }
                }
              }}
            >
              编辑合同金额
            </Button>
          )
        }
      >
        <Row gutter={16}>
          <Col span={6}>
            <Statistic
              title="合同金额"
              value={summary?.contract_amount || 0}
              precision={2}
              prefix="¥"
              valueStyle={{ color: '#1890ff' }}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="已收金额"
              value={summary?.total_received || 0}
              precision={2}
              prefix="¥"
              valueStyle={{ color: '#52c41a' }}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="待收金额"
              value={summary?.total_receivable || 0}
              precision={2}
              prefix="¥"
              valueStyle={{ color: '#faad14' }}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="净收款"
              value={summary?.net_received || 0}
              precision={2}
              prefix="¥"
              valueStyle={{ color: summary && (typeof summary.net_received === 'number' ? summary.net_received : parseFloat(String(summary.net_received))) >= 0 ? '#52c41a' : '#ff4d4f' }}
            />
          </Col>
        </Row>
      </Card>

      {/* 财务记录列表 */}
      <Card
        title="财务记录"
        bordered={false}
        extra={
          canEdit && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleOpenAddModal}
            >
              添加财务记录
            </Button>
          )
        }
      >
        <Table
          columns={columns}
          dataSource={records}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条记录`,
          }}
          locale={{
            emptyText: (
              <div style={{ padding: '40px 0', color: '#999' }}>
                <p>暂无财务记录</p>
                {canEdit && (
                  <Button
                    type="link"
                    icon={<PlusOutlined />}
                    onClick={handleOpenAddModal}
                  >
                    添加第一条财务记录
                  </Button>
                )}
              </div>
            ),
          }}
          scroll={{ x: 1200 }}
        />
      </Card>

      {/* 添加财务记录弹窗 */}
      <AddCaseFinanceRecordModal
        visible={modalVisible}
        caseId={caseId}
        onCancel={handleCloseAddModal}
        onSuccess={handleAddSuccess}
      />

      {/* 附件列表弹窗 */}
      <Modal
        title="附件列表"
        open={attachmentModalVisible}
        onCancel={() => setAttachmentModalVisible(false)}
        footer={null}
        width={600}
      >
        <List
          dataSource={selectedAttachments}
          renderItem={(attachment: any) => (
            <List.Item
              actions={[
                <Button
                  key="preview"
                  type="link"
                  icon={<EyeOutlined />}
                  onClick={() => handlePreviewAttachment(attachment)}
                >
                  预览
                </Button>,
                <Button
                  key="download"
                  type="link"
                  icon={<DownloadOutlined />}
                  onClick={() => handleDownloadAttachment(attachment)}
                >
                  下载
                </Button>
              ]}
            >
              <List.Item.Meta
                avatar={<PaperClipOutlined style={{ fontSize: 20 }} />}
                title={attachment.file_name}
                description={`大小: ${attachment.file_size ? (attachment.file_size / 1024).toFixed(2) + ' KB' : '未知'}`}
              />
            </List.Item>
          )}
        />
      </Modal>

      {/* 文件预览弹窗 */}
      {previewFile && (
        <FilePreview
          visible={previewVisible}
          fileUrl={previewFile.file_url}
          fileName={previewFile.original_filename}
          fileType={previewFile.content_type}
          onClose={() => {
            setPreviewVisible(false);
            setPreviewFile(null);
            // 重新打开附件列表
            setAttachmentModalVisible(true);
          }}
          onDownload={() => {
            const attachment = selectedAttachments.find(
              att => att.file_name === previewFile.original_filename
            );
            if (attachment) {
              handleDownloadAttachment(attachment);
            }
          }}
        />
      )}
    </div>
  );
};

export default CaseFinanceTab;
